import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, MessageCircle, ShieldCheck, Loader2 } from "lucide-react";
import { useApi, useFetch, errMsg } from "../lib/api";
import { Button } from "./ui/button";

export const Tick = ({ ok, testid }) => ok === true ? <CheckCircle2 size={14} className="text-emerald-400 inline" aria-label="Verified" data-testid={testid} />
  : ok === false ? <XCircle size={14} className="text-rose-400 inline" aria-label="Does not match" data-testid={testid} /> : null;

export const useKycStatus = () => useFetch("/kyc/status").data;

const Off = ({ text, testid }) => <p className="text-[11px] text-slate-500" data-testid={testid}>{text}</p>;

export const AadhaarVerify = ({ f, onResult, result }) => {
  const api = useApi();
  const st = useKycStatus();
  const [consent, setConsent] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (consent?.status !== "SENT") return undefined;
    timer.current = setInterval(async () => {
      try { const r = (await api.get(`/kyc/consent/${consent.id}`)).data; if (r.status !== "SENT") setConsent((c) => ({ ...c, ...r })); } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(timer.current);
  }, [api, consent?.id, consent?.status]);

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  const sendConsent = () => run(async () => {
    if (!f.mobile || !f.worker_full_name) throw new Error("Enter the worker's name and mobile number first");
    setConsent((await api.post("/kyc/consent", { mobile: f.mobile, worker_name: f.worker_full_name, language: f.preferred_language || null })).data);
    toast.success("Consent message sent on WhatsApp");
  });
  const sendOtp = () => run(async () => {
    if ((f.aadhaar_number || "").length !== 12) throw new Error("Enter the 12-digit Aadhaar number first");
    await api.post("/kyc/aadhaar/otp", { consent_id: consent.id, aadhaar_number: f.aadhaar_number });
    setConsent((c) => ({ ...c, otp_sent: true })); toast.success("UIDAI sent a code to the worker's Aadhaar-linked mobile");
  });
  const verify = () => run(async () => {
    const r = (await api.post("/kyc/aadhaar/verify", { consent_id: consent.id, otp, name: f.worker_full_name, address: f.address || null, dob: f.dob || null })).data;
    onResult(r); toast[r.ok ? "success" : "error"](r.ok ? "Aadhaar details match UIDAI" : "Some details do not match UIDAI");
  });

  if (st && !st.surepass) return <Off text="UIDAI check is not switched on yet (needs the Surepass key)." testid="aadhaar-verify-off" />;
  if (st && !st.whatsapp) return <Off text="UIDAI check needs WhatsApp consent, which is not switched on yet (needs the Twilio keys)." testid="aadhaar-verify-off" />;
  return (
    <div className="rounded-lg border border-[#1F2A3E] bg-[#0B1017] p-3 grid gap-2" data-testid="aadhaar-verify">
      <div className="flex items-center gap-2 text-xs text-slate-300"><ShieldCheck size={14} className="text-[#00E599]" />Check with UIDAI (name, date of birth, address)</div>
      {result && <p className={`text-xs ${result.ok ? "text-emerald-400" : "text-rose-400"}`} data-testid="aadhaar-verify-result">
        {result.ok ? "Verified with UIDAI." : `Does not fully match UIDAI${result.official_name ? ` (UIDAI name: ${result.official_name})` : ""}.`}</p>}
      {!result && !consent && <Button type="button" size="sm" variant="outline" disabled={busy} onClick={sendConsent} className="border-[#1F2A3E] w-fit" data-testid="aadhaar-send-consent">
        <MessageCircle size={13} className="mr-1.5" />Send consent request on WhatsApp</Button>}
      {consent?.status === "SENT" && <p className="text-xs text-amber-300 flex items-center gap-2" data-testid="aadhaar-consent-waiting"><Loader2 size={12} className="animate-spin" />Waiting for the worker to reply on WhatsApp (…{consent.mobile_last4})</p>}
      {consent?.status === "DECLINED" && <p className="text-xs text-rose-400" data-testid="aadhaar-consent-declined">The worker declined. You can still add them without the UIDAI check.</p>}
      {consent?.status === "AGREED" && !result && !consent.otp_sent && <Button type="button" size="sm" disabled={busy} onClick={sendOtp} className="bg-[#00E599] text-[#0B1017] w-fit" data-testid="aadhaar-send-otp">Worker agreed · send Aadhaar code</Button>}
      {consent?.otp_sent && !result && <div className="flex gap-2">
        <input className="c-input" placeholder="Code the worker received" inputMode="numeric" maxLength={8} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid="aadhaar-otp-input" />
        <Button type="button" size="sm" disabled={busy || otp.length < 4} onClick={verify} className="bg-[#00E599] text-[#0B1017]" data-testid="aadhaar-otp-verify">Verify</Button></div>}
    </div>);
};

export const IdVerify = ({ kind, number, name, onResult, result }) => {
  const api = useApi();
  const st = useKycStatus();
  const [busy, setBusy] = useState(false);
  const pan = kind === "PAN";
  if (st && (!st.surepass || (!pan && !st.esic))) return <Off text={`${pan ? "PAN" : "ESIC"} check is not switched on yet.`} testid={`${kind.toLowerCase()}-verify-off`} />;
  const go = async () => {
    setBusy(true);
    try {
      const r = (await api.post(pan ? "/kyc/pan" : "/kyc/esic", pan ? { pan_number: number, name } : { esic_ip_number: number, name })).data;
      onResult(r); toast[r.ok ? "success" : "error"](r.ok ? `${kind} and name verified` : `${kind} details do not match`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`${kind.toLowerCase()}-verify`}>
      <button type="button" disabled={busy || !number || !name} onClick={go} className="text-[#00E599] hover:underline disabled:opacity-40" data-testid={`${kind.toLowerCase()}-verify-btn`}>
        {busy ? "Checking…" : `Verify with ${pan ? "Income Tax" : "ESIC"}`}</button>
      {result && <span className={result.ok ? "text-emerald-400" : "text-rose-400"} data-testid={`${kind.toLowerCase()}-verify-result`}>
        {result.ok ? "Verified" : result.number_ok ? `Name differs (official: ${result.official_name})` : "Number not found"}</span>}
    </div>);
};
